// External
import { useCallback } from 'react';
import { useStoreState } from '@/store';

// Internal
import { useStoreActions } from '@/store';
import { queryCheckoutSession } from '@/lib/graphql';
import { getToken } from '@/lib/globals';
import { features } from '@/lib/features';
import { logger } from '@/lib/logger';
import { noop } from '@/lib/utils/miscUtils';
import {
  getSelectedShippingMethod,
  getShippingAddress,
  getShippingCountry,
  mapAddressValues,
} from '@/lib/utils/shipping';
import type {
  EmptyObject,
  MappedAddressValues,
  OnShippingChangeCallbackWrapper,
  OnShippingChangeWrapper,
  OnShippingChangeWrapperPayload,
  ShippingOption,
} from '@/lib/utils/shipping/types';
import { SHIPPING_CALLBACK_ERROR_MAPPING } from '@/lib/utils/shipping/constants';
import {
  logOnShippingAddressChangeReceivedToFPTI,
  logShippingOptionsChangeReceivedToFPTI,
} from '@/lib/utils/shipping/fpti';
import { useShippingChangeCallback } from './useShippingChangeCallback';
import { conditionallyFetchConversionData } from '@/lib/graphql/operations/currencyConversion';

/**
 * @NOTE
 * This is the hook that will be utilized by other components, such as
 *  `Shipping`, and `Address`.
 *
 * {@link https://engineering.paypalcorp.com/confluence/pages/viewpage.action?pageId=853380692}
 */
export const useShippingChange = (): OnShippingChangeWrapper | typeof noop => {
  const cartId: string = getToken();
  const cartDetails = useStoreState(({ api }) => api.cartDetails);
  const shippingMethods = useStoreState(({ api }) => api.shippingMethods);

  const setCartData = useStoreActions(({ api }) => api.setCartData);
  const setPageLevelError = useStoreActions(({ ui }) => ui.setPageLevelError);
  const setShowLoadingSpinnerWithMessage = useStoreActions(
    ({ ui }) => ui.setShowLoadingSpinnerWithMessage,
  );
  const fiType = useStoreState(({ ui }) => ui.fiType);

  const isBankForm = fiType === 'bank' && features.isActive('hasAddBank');
  const isShippingCallbackV1Eligible: boolean = features.isActive(
    'isShippingCallbackV1Eligible',
  );
  const { amounts } = cartDetails;

  const onShippingChangeCbWrapper: OnShippingChangeCallbackWrapper =
    // @ts-expect-error Fix cartDetails
    useShippingChangeCallback(cartId, amounts);

  const execute = useCallback(
    async (payload?: OnShippingChangeWrapperPayload): Promise<void> => {
      // `isViaShippingOptionsChange` indicates whether or not the
      // shipping callback was invoked by a shipping option change.
      const { isViaShippingOptionsChange = false } = payload ?? {};
      const selectedShippingOption: ShippingOption | undefined =
        // @ts-expect-error Fix cartDetails
        getSelectedShippingMethod(shippingMethods);
      const logShippingChangeReceivedToFPTI = isViaShippingOptionsChange
        ? logShippingOptionsChangeReceivedToFPTI
        : logOnShippingAddressChangeReceivedToFPTI;
      const spinnerMessage: string = isViaShippingOptionsChange
        ? 'spinner.fetchingDelivery'
        : 'spinner.fetchingShipping';
      let didCallbackFire = false;

      try {
        const shippingAddress = {
          ...getShippingAddress(),
          country: { value: getShippingCountry() },
        };
        const formattedShippingAddress: MappedAddressValues | EmptyObject =
          mapAddressValues(shippingAddress);

        // Fire onShippingChange callback
        didCallbackFire = Boolean(
          await onShippingChangeCbWrapper({
            formattedShippingAddress,
            isViaShippingOptionsChange,
            selectedShippingOption,
            setShowLoadingSpinnerWithMessage,
            spinnerMessage,
          }),
        );
      } catch (err: Error | unknown) {
        setShowLoadingSpinnerWithMessage(null);
        // Errors in this catch block means that the merchant
        // does not support the buyer's address.
        setPageLevelError('fieldErrors.shipping.addressReject');

        const errMsg = err instanceof Error ? err?.message : err;

        logShippingChangeReceivedToFPTI({
          errorDescription: errMsg,
          status: 'failure',
        });
        logger.error('on_shipping_change_callback_known_error', {
          error_description: errMsg,
        });
        window.scrollTo(0, 0);
      }

      try {
        // We only want to refetch the checkoutSession and the
        // currency conversion queries if callback has been run
        // to avoid UI latency.
        if (didCallbackFire) {
          // The checkoutSession and currency conversion queries
          // do NOT depend on each other; hence, invoking them
          // in parallel is fine.
          const [checkoutSessionData] = await Promise.all([
            queryCheckoutSession(),
            // There are two currency conversion queries:
            //   1. Credit card - for all countries
            //   2. Bank - as of now, only for `Germany` and `Japan`
            // The bank form component handles refetching the currency
            // conversion data; hence, we're returning a resolved promise.
            isBankForm
              ? Promise.resolve()
              : conditionallyFetchConversionData(null, true),
          ]);
          const { cart } = checkoutSessionData ?? {};
          const { amounts, items, shippingMethods } = cart ?? {};

          setCartData({
            cartAmount: amounts?.total,
            cartDetails: {
              ...cartDetails,
              // @ts-expect-error Fix cartDetails
              amounts,
              // @ts-expect-error Fix cartDetails
              items,
            },
            shippingMethods: shippingMethods ?? [],
          });
          logShippingChangeReceivedToFPTI({ status: 'success' });
          logger.info('on_shipping_change_callback_is_called', {
            isViaShippingOptionsChange,
            selectedShippingOption,
          });

          // Clear spinner and error message
          setShowLoadingSpinnerWithMessage(null);
          setPageLevelError('');
        }
      } catch (err: Error | unknown) {
        // Clear spinner
        setShowLoadingSpinnerWithMessage(null);
        const errMsg: string | unknown =
          err instanceof Error ? err?.message : err;
        // Handle generic error
        setPageLevelError(SHIPPING_CALLBACK_ERROR_MAPPING.DEFAULT);

        logShippingChangeReceivedToFPTI({
          errorDescription: SHIPPING_CALLBACK_ERROR_MAPPING.DEFAULT,
          status: 'failure',
        });
        logger.error('on_shipping_change_callback_generic_error', {
          error_description: errMsg,
        });
        window.scrollTo(0, 0);
      }
    },
    [onShippingChangeCbWrapper, shippingMethods, isBankForm],
  );

  if (!isShippingCallbackV1Eligible) {
    logger.info('cssc_v1_shipping_callback_not_eligible', {
      cartId,
    });
    return noop;
  }

  return execute;
};
